import { Fragment, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { DatasourceTable, newTableTheme } from '@ardoq/table-legacy';
import { colors } from '@ardoq/design-tokens';
import {
  CheckboxCheckedIcon,
  CheckboxMixedIcon,
  CheckboxUncheckedIcon,
} from '@ardoq/icons';
import { Datasource } from './types';
import {
  PopoverContentType,
  WithPopover,
  TruncatedTextPopover,
} from '@ardoq/popovers';

import { EmptyState, EmptyStateNoResultIllustration } from '@ardoq/status-ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';

type FieldTableProps<T extends object> = {
  i18nLocale: I18nLocale;
  onRemoveAll: (() => void) | null;
  onAddAll: (() => void) | null;
  onFieldClick: (field: string) => void;
  fields: T[];
  labelColumnTitle: string;
  idColumnTitle: string;
  selectedFields: Set<string>;
  requiredFieldsPopoverContent: Record<string, PopoverContentType>;
  visibleNameColumn?: boolean;
  tableName?: string | null;
  isLoading?: boolean;
  noResultsTitle?: string;
  noResultsDescription?: string;
};

const BodyTr = styled(newTableTheme.BodyTr)<{ $isDisabled?: boolean }>`
  cursor: ${props => (props.$isDisabled ? 'not-allowed' : 'pointer')};

  ${props =>
    props.$isDisabled &&
    css`
      opacity: 0.5;
    `}

  td {
    min-width: 0;
    line-height: 40px;
  }
  td:first-child {
    display: flex;
    align-items: center;
  }
`;

const Table = styled(newTableTheme.Table)`
  tbody {
    transform: translate(0);
  }
  table-layout: fixed;
  colgroup col:first-child {
    width: 40px;
  }
  input {
    cursor: pointer;
  }
`;

const CheckboxChecked = styled(CheckboxCheckedIcon)`
  cursor: pointer;
`;

const CheckboxUnchecked = styled(CheckboxUncheckedIcon).attrs({
  color: colors.iconAction,
})`
  cursor: pointer;
`;

const CheckboxFlat = styled(CheckboxMixedIcon)`
  cursor: pointer;
`;

export const FieldsTable = <T extends Datasource>({
  i18nLocale,
  fields,
  onRemoveAll,
  onAddAll,
  onFieldClick,
  labelColumnTitle,
  idColumnTitle,
  visibleNameColumn = true,
  tableName,
  selectedFields,
  requiredFieldsPopoverContent,
  isLoading = false,
  noResultsTitle,
  noResultsDescription,
}: FieldTableProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: fields.length,
    getScrollElement: () =>
      tableContainerRef.current?.querySelector<HTMLTableElement>(
        'div:has(table)'
      ) || null,
    estimateSize: () => 40,
    overscan: 10,
  });

  useEffect(() => {
    const tableBodyElement =
      tableContainerRef.current?.querySelector<HTMLTableSectionElement>(
        'div:has(table) tbody'
      );
    if (tableBodyElement && tableBodyElement.style) {
      tableBodyElement.style.height = `${virtualizer.getTotalSize()}px`;
      tableBodyElement.style.position = 'relative';
    }
  }, [fields, virtualizer]);

  const items = virtualizer
    .getVirtualItems()
    .map(virtualItem => ({ ...fields[virtualItem.index], ...virtualItem }));

  const getRowComponent = (row: Datasource) => {
    return (props: any) => (
      <WithPopover content={row.isDisabled ? row.disabledTooltip : undefined}>
        <BodyTr $isDisabled={row.isDisabled} {...props}></BodyTr>
      </WithPopover>
    );
  };

  const handleRowClick = (row: Datasource) => {
    if (!row.isDisabled && !requiredFieldsPopoverContent[row.id]) {
      onFieldClick(row.id);
    }
  };

  // Fields that can be toggled by the user, those which are not disabled and not required.
  const toggleableFields = useMemo(
    () =>
      fields.filter(
        field => !field.isDisabled && !requiredFieldsPopoverContent[field.id]
      ),
    [fields, requiredFieldsPopoverContent]
  );

  const toggleableFieldCount = toggleableFields.length;

  // Number of toggleable fields which are currently selected.
  const toggleableSelectedCount = useMemo(
    () => toggleableFields.filter(field => selectedFields.has(field.id)).length,
    [toggleableFields, selectedFields]
  );

  return (
    <div ref={tableContainerRef}>
      <DatasourceTable
        fixedHeader
        loading={isLoading}
        dataSource={items}
        components={{ ...newTableTheme, Table }}
        getRowComponent={getRowComponent}
        tableName={tableName}
        style={{ height: '600px', maxHeight: '600px', overflowY: 'auto' }}
        renderEmptyTable={() =>
          !isLoading ? (
            <FlexBox align="center" justify="center">
              <EmptyState
                title={
                  noResultsTitle ??
                  t(
                    i18nLocale,
                    'integrations:fieldSelector.noResultsTitle',
                    { label: labelColumnTitle.toLowerCase() },
                    'No {{label}} found'
                  )
                }
                description={
                  noResultsDescription ??
                  t(
                    i18nLocale,
                    'integrations:fieldSelector.noResultsDescription',
                    { label: labelColumnTitle.toLowerCase() },
                    "There's not {{label}} to show with current selection."
                  )
                }
                media={<EmptyStateNoResultIllustration />}
              />
            </FlexBox>
          ) : (
            <Fragment />
          )
        }
        columns={[
          {
            dataIndex: 'name',
            headerRender: () => {
              if (toggleableFieldCount === 0) return null;

              /* Why not checkbox input? That causes the performance issues when rendered for every row
                 The second problem is the checkbox from the design system has isMixed prop which
                 trigger onChange for every row and also it results in checkall rather than
                 uncheck all when clicking on mixed checkbox
                 So we use custom checkbox here */

              if (toggleableSelectedCount === 0)
                return onAddAll ? (
                  <CheckboxUnchecked deprecated_onClick={onAddAll} />
                ) : null;

              if (toggleableSelectedCount === toggleableFieldCount)
                return onRemoveAll ? (
                  <CheckboxChecked
                    color={colors.iconActionActive}
                    deprecated_onClick={onRemoveAll}
                  />
                ) : null;

              return onRemoveAll ? (
                <CheckboxFlat
                  color={colors.iconActionActive}
                  deprecated_onClick={onRemoveAll}
                />
              ) : null;
            },
            valueRender: (_name: string, row) => (
              <>
                {requiredFieldsPopoverContent[row.id] && (
                  <WithPopover content={requiredFieldsPopoverContent[row.id]}>
                    <CheckboxChecked color={colors.iconActionDisabled} />
                  </WithPopover>
                )}
                {!requiredFieldsPopoverContent[row.id] && (
                  <>
                    {selectedFields.has(row.id) ? (
                      <CheckboxChecked color={colors.iconActionActive} />
                    ) : (
                      <CheckboxUnchecked />
                    )}
                  </>
                )}
              </>
            ),
          },
          {
            title: labelColumnTitle,
            dataIndex: 'label',
            valueRender: (label: string) => (
              <TruncatedTextPopover>{label}</TruncatedTextPopover>
            ),
          },
          ...(visibleNameColumn
            ? [
                {
                  title: idColumnTitle,
                  dataIndex: 'name',
                  valueRender: (name: string) => (
                    <TruncatedTextPopover>{name}</TruncatedTextPopover>
                  ),
                },
              ]
            : []),
        ]}
        onRowClick={handleRowClick}
        rowStyle={row => ({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `40px 1fr ${visibleNameColumn ? '1fr' : ''}`,
          alignItems: 'center',
          height: `${row.size}px`,
          transform: `translateY(${row.start}px)`,
          overflow: 'hidden',
        })}
      />
    </div>
  );
};
